import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import IntervalInput, {
  parseIntervalInput,
  intervalToLatex,
} from "../../../../components/inputs/IntervalInput";
import NumberLineRanges from "../../../../components/visuals/NumberLineRanges";

/* ========== helpers ========== */
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
const randInt = (a, b) => a + Math.floor(Math.random() * (b - a + 1));

const opLatex = (op) => (op === "≤" ? "\\le" : op === "≥" ? "\\ge" : op);

// x < a  ∨  x ≥ b
function unionIneqLatex(leftOp, a, rightOp, b) {
  return `x\\,${opLatex(leftOp)}\\,${a}\\;\\lor\\;x\\,${opLatex(rightOp)}\\,${b}`;
}

function expectedParsed(leftOp, a, rightOp, b) {
  const left = { left: -Infinity, right: a, leftClosed: false, rightClosed: leftOp === "≤" };
  const right = { left: b, right: Infinity, leftClosed: rightOp === "≥", rightClosed: false };
  return { kind: "intervals", intervals: [left, right] };
}

function sameUnion(parsed, expect) {
  if (!parsed || parsed.kind !== "intervals") return false;
  const A = (parsed.intervals || []).slice().sort((p, q) => (p.left === -Infinity ? -1 : p.left - q.left));
  const B = (expect.intervals || []).slice().sort((p, q) => (p.left === -Infinity ? -1 : p.left - q.left));
  if (A.length !== B.length) return false;

  const eqN = (x, y) =>
    (x === Infinity && y === Infinity) ||
    (x === -Infinity && y === -Infinity) ||
    (Number.isFinite(x) && Number.isFinite(y) && Math.abs(x - y) <= 1e-9);

  for (let i = 0; i < A.length; i++) {
    const P = A[i], Q = B[i];
    if (!(eqN(P.left, Q.left) && eqN(P.right, Q.right) && P.leftClosed === Q.leftClosed && P.rightClosed === Q.rightClosed)) {
      return false;
    }
  }
  return true;
}

function rawUnion(leftOp, a, rightOp, b) {
  const L = leftOp === "≤" ? `(-∞, ${a}]` : `(-∞, ${a})`;
  const R = rightOp === "≥" ? `[${b}, ∞)` : `(${b}, ∞)`;
  return `${L} ∪ ${R}`;
}

/* ========== generator ========== */
export function generateTask() {
  // Wymuszamy rozłączność: a < b
  const a = randInt(-9, 6);
  const b = randInt(a + 2, 9); // min. „dziura” długości ≥1
  const leftOp = pick(["<", "≤"]);
  const rightOp = pick([">", "≥"]);
  return { type: "InequalityUnionOneSidedTask", data: { a, b, leftOp, rightOp } };
}

/* ========== render: treść ========== */
export function renderTask({ data }) {
  const { a, b, leftOp, rightOp } = data;
  return (
    <div style={{ fontSize: 16, lineHeight: 1.5, marginBottom: 12 }}>
      <span>Zapisz nierówność w formie przedziału: </span>
      <span style={{ marginLeft: 8 }}>
        <InlineMath math={unionIneqLatex(leftOp, a, rightOp, b)} />
      </span>
    </div>
  );
}

/* ========== input (etykieta x ∈ + IntervalInput) ========== */
export function renderInput(task, userAnswer, onChange, disabled) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-1" style={{ marginBottom: "0.6rem" }}>
        <div style={{ lineHeight: 1 }}>
          <InlineMath math={"x\\;\\in"} />
        </div>
        <IntervalInput
          value={userAnswer}
          onChange={onChange}
          disabled={disabled}
          asString
          previewWithX
          placeholder="np. (-∞, 5) ∪ [7, ∞)"
        />
      </div>
    </div>
  );
}

/* ========== sprawdzanie ========== */
export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer) return false;
  const parsed =
    typeof userAnswer === "string" ? parseIntervalInput(userAnswer) : userAnswer.parsed;
  const expect = expectedParsed(data.leftOp, data.a, data.rightOp, data.b);
  return sameUnion(parsed, expect);
}

/* ========== odpowiedzi ========== */
export function renderUserAnswer(userAnswer) {
  if (!userAnswer || (typeof userAnswer === "object" && !userAnswer.raw)) return <i>(brak odpowiedzi)</i>;
  const raw = typeof userAnswer === "string" ? userAnswer : userAnswer.raw;
  return <InlineMath math={`x\\in\\;${intervalToLatex(raw)}`} />;
}

export function renderAnswer({ data }) {
  const { leftOp, a, rightOp, b } = data;
  return <InlineMath math={`x\\in\\;${intervalToLatex(rawUnion(leftOp, a, rightOp, b))}`} />;
}

/* ========== wyjaśnienie: Krok 1 (od razu x ∈ …), Krok 2: grafika ========== */
export function renderExplanation({ data }) {
  const { a, b, leftOp, rightOp } = data;

  const leftClosed = leftOp === "≤";
  const rightClosed = rightOp === "≥";
  const lb = leftClosed ? "]" : ")";
  const rb = rightClosed ? "[" : "("; // tu tylko opis; raw budujemy niżej

  const rule = `Lewy warunek (${leftOp === "≤" ? "≤" : "<"}) daje przedział do ${a} ${leftClosed ? "z domknięciem" : "bez domknięcia"} po prawej; prawy (${rightOp === "≥" ? "≥" : ">"}) — od ${b} ${rightClosed ? "z domknięciem" : "bez domknięcia"} po lewej. Przy ±∞ zawsze nawias okrągły.`;

  const raw = rawUnion(leftOp, a, rightOp, b);

  return (
    <div className="space-y-2">
      <p><strong>Krok 1.</strong> {rule}</p>
      <div style={{ textAlign: "center", marginBottom: "0.5rem" }}>
        <BlockMath math={`x\\in\\;${intervalToLatex(raw)}`} />
      </div>

      <p><strong>Krok 2.</strong> Zaznaczamy oba przedziały na osi liczbowej.</p>
      <div style={{ maxWidth: 620, margin: "0 auto" }}>
        <NumberLineRanges
          showZero
          ranges={[
            { from: -Infinity, to: a, leftClosed: false, rightClosed: leftClosed, rightLabelLatex: `${a}` },
            { from: b, to: Infinity, leftClosed: rightClosed, rightClosed: false, leftLabelLatex: `${b}` },
          ]}
        />
      </div>
    </div>
  );
}
